import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Menu,
  Message,
  Segment,
} from "semantic-ui-react";
import { useMutation, useQuery } from "@apollo/client";

import { PATHS } from "../../../paths";
import { useIsAdminLoading } from "../../../hooks/isAdmin";
import { GET_PARTNERSHIPS } from "../../../queries/getPartnerships";
import { UPDATE_PARTNERSHIP } from "../../../mutations/UPDATE_PARTNERSHIP";
import { UPDATE_PARTNERSHIP_PROGRAMS } from "../../../mutations/UPDATE_PARTNERSHIP_PROGRAMS";
import { advertisedAgo, volunteerCallComposeUrl } from "../../../utils/volunteerCall";

// ── Advertise in feed ────────────────────────────────────────────────────────

/**
 * Sends the community to the composer with a Volunteering Opportunity already set up for this
 * role. Re-advertising is allowed — it's the community's call — but the last time is shown so
 * nobody repeats themselves by accident.
 */
function AdvertiseControl({
  saved,
  lastAdvertisedAt,
  pendingAdvertisement,
  onAdvertise,
}: {
  saved: boolean;
  lastAdvertisedAt?: string | null;
  pendingAdvertisement?: boolean | null;
  onAdvertise: () => void;
}) {
  const ago = advertisedAgo(lastAdvertisedAt);
  return (
    <div style={{ marginTop: "0.5em", paddingLeft: "0.25em", display: "flex", alignItems: "center", gap: "0.75em", flexWrap: "wrap" }}>
      <Button
        size="tiny"
        color="teal"
        basic
        icon="bullhorn"
        type="button"
        content="Advertise in feed"
        disabled={!saved}
        onClick={onAdvertise}
      />
      {!saved && (
        <span style={{ fontSize: "0.85em", color: "rgba(0,0,0,0.6)" }}>Save this opportunity first.</span>
      )}
      {pendingAdvertisement && (
        <Label size="tiny" color="yellow">
          <Icon name="clock outline" />
          An advertisement is waiting for review or scheduled
        </Label>
      )}
      {ago && (
        <Label size="tiny" color="orange" basic>
          <Icon name="history" />
          Advertised {ago}
        </Label>
      )}
    </div>
  );
}

// ── Interest readout ─────────────────────────────────────────────────────────

/**
 * Who has volunteered for a role. Members express interest from the iOS app; this is the
 * only place it surfaces to staff, so it renders even when the count is zero.
 */
function InterestReadout({
  count,
  interested,
}: {
  count?: number | null;
  interested?: { id: number; expressedAt: string; person: { id: number; name: string } | null }[] | null;
}) {
  const total = count ?? 0;
  return (
    <div style={{ marginTop: "0.25em", paddingLeft: "0.25em" }}>
      <Label size="tiny" color={total > 0 ? "green" : undefined} basic={total === 0}>
        <Icon name="hand paper outline" />
        {total} {total === 1 ? "person interested" : "people interested"}
      </Label>
      {interested && interested.length > 0 && (
        <span style={{ marginLeft: "0.75em", fontSize: "0.9em", color: "rgba(0,0,0,0.6)" }}>
          {interested.map((i) => i.person?.name ?? "Unknown").join(", ")}
        </span>
      )}
    </div>
  );
}

// ── Types ────────────────────────────────────────────────────────────────────

type ServerInterest = { id: number; expressedAt: string; person: { id: number; name: string } | null };
type ServerVolunteerRole = {
  id: number;
  title: string;
  type: string | null;
  icon: string | null;
  interestedCount?: number | null;
  interested?: ServerInterest[] | null;
  lastAdvertisedAt?: string | null;
  pendingAdvertisement?: boolean | null;
};
type ServerEmploymentCategory = { id: number; name: string; shortName: string | null; count: number };
type ServerStat = { label: string; value: string };
type ServerProgram = { card: string; description: string | null; stats: ServerStat[] | null };

type Partnership = {
  id: number;
  supporterOrganisationId: number;
  supporterName: string;
  scope: string;
  scopeLabel?: string | null;
  description?: string | null;
  workforceTotal?: number | null;
  volunteerRoles?: ServerVolunteerRole[] | null;
  employmentCategories?: ServerEmploymentCategory[] | null;
  programs?: ServerProgram[] | null;
};

// Local editable rows carry a client-only rowId for React keys / edit targeting.
// `serverId` is the persisted VolunteerRole.id, absent for a row added in this session.
// It must be sent back on save: the backend reconciles by id, and a role that arrives
// without one is treated as new — which would discard the interest people expressed in it.
type VolunteerRow = {
  rowId: number;
  serverId?: number;
  title: string;
  type: string;
  icon: string;
  interestedCount?: number | null;
  interested?: ServerInterest[] | null;
  lastAdvertisedAt?: string | null;
  pendingAdvertisement?: boolean | null;
};
type EmploymentRow = { rowId: number; name: string; shortName: string; count: number };
type StatRow = { label: string; value: string };
type ProgramState = Record<string, { description: string; stats: StatRow[] }>;

const VOLUNTEER_TYPES = ["Fellowship", "Project", "Internship"];

// Curated SF Symbol names that render in the iOS app, with friendly labels.
const VOLUNTEER_ICONS: { value: string; text: string }[] = [
  { value: "book.fill", text: "Tutoring / Books" },
  { value: "graduationcap.fill", text: "Education" },
  { value: "person.2.fill", text: "Mentoring / People" },
  { value: "figure.run", text: "Sports" },
  { value: "fork.knife", text: "Food / Kitchen" },
  { value: "heart.fill", text: "Care" },
  { value: "hands.sparkles", text: "Helping hands" },
  { value: "cross.case.fill", text: "Medical" },
  { value: "hammer.fill", text: "Trades / Construction" },
  { value: "paintbrush.fill", text: "Arts & Crafts" },
  { value: "music.note", text: "Music" },
  { value: "leaf.fill", text: "Gardening / Environment" },
  { value: "house.fill", text: "Housing" },
  { value: "cart.fill", text: "Market / Shopping" },
  { value: "briefcase.fill", text: "Admin / Work" },
  { value: "megaphone.fill", text: "Outreach" },
  { value: "camera.fill", text: "Media" },
  { value: "car.fill", text: "Transport / Driving" },
  { value: "globe", text: "Community" },
  { value: "pawprint.fill", text: "Animals" },
];

const iconOptions = (current: string) => {
  const base = VOLUNTEER_ICONS.map((i) => ({ key: i.value, value: i.value, text: `${i.text} (${i.value})` }));
  if (current && !VOLUNTEER_ICONS.some((i) => i.value === current)) {
    base.unshift({ key: current, value: current, text: `${current} (custom)` });
  }
  return base;
};

// The three leadership program panels, with default labels/values mirroring the iOS app.
const PROGRAM_CARDS: { card: string; title: string; description: string; stats: StatRow[] }[] = [
  {
    card: "BUSINESS",
    title: "Business",
    description:
      "Revenue tracking 12% below annual target. Market stall expansion in progress with 3 active ventures employing community members.",
    stats: [
      { label: "Annual Budget", value: "$48,000" },
      { label: "Budget Used", value: "78%" },
      { label: "Revenue YTD", value: "$52,400" },
      { label: "vs Target", value: "−12%" },
      { label: "Active Ventures", value: "3" },
      { label: "Jobs Created", value: "7" },
    ],
  },
  {
    card: "COLLECTIVE",
    title: "Collective",
    description:
      "Cross-org collaboration up 40% this quarter. Eight partner organisations engaged across five active shared projects.",
    stats: [
      { label: "Program Budget", value: "$12,000" },
      { label: "Budget Used", value: "45%" },
      { label: "Active Members", value: "24" },
      { label: "Growth (Qtr)", value: "+3" },
      { label: "Active Projects", value: "5" },
      { label: "Partners", value: "8" },
    ],
  },
  {
    card: "NEIGHBOURING",
    title: "Neighbouring",
    description:
      "Building authentic relationships in the surrounding neighbourhood. Tracking friendships, people of peace, and organic community connections.",
    stats: [
      { label: "Friendships", value: "18" },
      { label: "People of Peace", value: "6" },
      { label: "Connections", value: "34 this qtr" },
      { label: "New This Month", value: "4" },
      { label: "Active Ongoing", value: "12" },
      { label: "Follow-ups Due", value: "3" },
    ],
  },
];

// Each card always shows exactly six labelled values.
const ensureSix = (stats: StatRow[]): StatRow[] => {
  const out = stats.slice(0, 6).map((s) => ({ label: s.label ?? "", value: s.value ?? "" }));
  while (out.length < 6) out.push({ label: "", value: "" });
  return out;
};

/** The editable content of the page, ignoring client-only row ids — for unsaved-edit detection. */
const snapshotOf = (
  desc: string,
  workforce: number | "",
  roles: VolunteerRow[],
  employment: EmploymentRow[],
  programs: ProgramState
): string =>
  JSON.stringify({
    desc,
    workforce,
    roles: roles.map((r) => [r.serverId ?? null, r.title, r.type, r.icon]),
    employment: employment.map((c) => [c.name, c.shortName, Number(c.count) || 0]),
    programs,
  });

// ── Component ──────────────────────────────────────────────────────────────────

export function Partnerships() {
  const [isAdmin, adminLoading] = useIsAdminLoading();
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery(GET_PARTNERSHIPS, { skip: !isAdmin });
  const partnerships: Partnership[] = useMemo(() => data?.partnerships ?? [], [data]);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"info" | "programs">("info");

  // Community-info buffers, seeded from server data.
  const [desc, setDesc] = useState("");
  const [workforce, setWorkforce] = useState<number | "">("");
  const [volunteerRoles, setVolunteerRoles] = useState<VolunteerRow[]>([]);
  const [employment, setEmployment] = useState<EmploymentRow[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState(false);

  // Programs buffers.
  const [programState, setProgramState] = useState<ProgramState>({});
  const [programError, setProgramError] = useState<string | null>(null);
  const [programSaved, setProgramSaved] = useState(false);

  // What the buffers held when last seeded from the server, so leaving for the composer can
  // warn about unsaved edits rather than silently dropping them.
  const seededSnapshot = useRef<string | null>(null);

  const rowIdRef = useRef(1);
  const nextRowId = () => rowIdRef.current++;

  // Auto-select when there is exactly one partnership.
  useEffect(() => {
    if (selectedId === null && partnerships.length === 1) {
      setSelectedId(partnerships[0].id);
    }
  }, [partnerships, selectedId]);

  // Seed all editable buffers from server data whenever the selection changes or the
  // underlying data reloads (initial load / post-save refetch).
  useEffect(() => {
    const p = partnerships.find((x) => x.id === selectedId);
    if (!p) return;
    const seededDesc = p.description ?? "";
    const seededWorkforce: number | "" = p.workforceTotal ?? "";
    setDesc(seededDesc);
    setWorkforce(seededWorkforce);
    const seededRoles: VolunteerRow[] = (p.volunteerRoles ?? []).map((r) => ({
        rowId: nextRowId(),
        serverId: r.id,
        title: r.title ?? "",
        type: r.type ?? VOLUNTEER_TYPES[0],
        icon: r.icon ?? "",
        interestedCount: r.interestedCount,
        interested: r.interested,
        lastAdvertisedAt: r.lastAdvertisedAt,
        pendingAdvertisement: r.pendingAdvertisement,
      }));
    setVolunteerRoles(seededRoles);
    const seededEmployment: EmploymentRow[] = (p.employmentCategories ?? []).map((c) => ({
      rowId: nextRowId(),
      name: c.name ?? "",
      shortName: c.shortName ?? "",
      count: c.count ?? 0,
    }));
    setEmployment(seededEmployment);

    const seeded: ProgramState = {};
    for (const c of PROGRAM_CARDS) {
      const existing = (p.programs ?? []).find((pr) => pr.card === c.card);
      seeded[c.card] = existing
        ? { description: existing.description ?? "", stats: ensureSix(existing.stats ?? []) }
        : { description: c.description, stats: ensureSix(c.stats) };
    }
    setProgramState(seeded);
    // Taken from the values just seeded, not from state — which won't hold them until the
    // next render.
    seededSnapshot.current = snapshotOf(seededDesc, seededWorkforce, seededRoles, seededEmployment, seeded);
  }, [selectedId, partnerships]);

  const hasUnsavedEdits = seededSnapshot.current !== null
    && seededSnapshot.current !== snapshotOf(desc, workforce, volunteerRoles, employment, programState);

  const advertise = (roleId: number) => {
    if (hasUnsavedEdits && !window.confirm("You have unsaved changes on this page. Leave without saving them?")) {
      return;
    }
    navigate(volunteerCallComposeUrl(PATHS.POSTS_CREATE, [roleId]));
  };

  const [updatePartnership, { loading: saving }] = useMutation(UPDATE_PARTNERSHIP);
  const [updatePrograms, { loading: savingPrograms }] = useMutation(UPDATE_PARTNERSHIP_PROGRAMS);

  // Gate: non-admins don't belong here. Wait for the admin check so a real admin
  // isn't bounced during the initial load.
  if (!adminLoading && !isAdmin) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const scopeText = (p: Partnership) =>
    p.scope === "*" ? "Whole organisation" : p.scopeLabel || p.scope;

  const options = partnerships.map((p) => ({
    key: p.id,
    value: p.id,
    text: `${p.supporterName} — ${scopeText(p)}`,
  }));

  const supporterNames = Array.from(new Set(partnerships.map((p) => p.supporterName)));
  const audienceSuffix =
    supporterNames.length > 0
      ? ` the organisations that support you: ${supporterNames.join(", ")}`
      : " the organisations that support you";
  const selected = partnerships.find((p) => p.id === selectedId) ?? null;
  const totalEmployed = employment.reduce((sum, c) => sum + (Number(c.count) || 0), 0);

  // ── Community-info mutators ──
  const updateVol = (rowId: number, patch: Partial<VolunteerRow>) =>
    setVolunteerRoles((rows) => rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
  const addVol = () =>
    setVolunteerRoles((rows) => [...rows, { rowId: nextRowId(), title: "", type: VOLUNTEER_TYPES[0], icon: "" }]);
  const removeVol = (rowId: number) =>
    setVolunteerRoles((rows) => rows.filter((r) => r.rowId !== rowId));

  const updateEmp = (rowId: number, patch: Partial<EmploymentRow>) =>
    setEmployment((rows) => rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
  const addEmp = () =>
    setEmployment((rows) => [...rows, { rowId: nextRowId(), name: "", shortName: "", count: 0 }]);
  const removeEmp = (rowId: number) =>
    setEmployment((rows) => rows.filter((r) => r.rowId !== rowId));

  // ── Program mutators ──
  const setProgramDesc = (card: string, description: string) =>
    setProgramState((s) => ({ ...s, [card]: { ...s[card], description } }));
  const setProgramStat = (card: string, index: number, patch: Partial<StatRow>) =>
    setProgramState((s) => ({
      ...s,
      [card]: { ...s[card], stats: s[card].stats.map((st, i) => (i === index ? { ...st, ...patch } : st)) },
    }));

  const handleSave = async () => {
    if (selectedId == null) return;
    setSaveError(null);
    try {
      await updatePartnership({
        variables: {
          supporterId: selectedId,
          description: desc.trim() ? desc : null,
          workforceTotal: workforce === "" ? null : Number(workforce),
          volunteerRoles: volunteerRoles
            .filter((r) => r.title.trim())
            .map((r) => ({ id: r.serverId ?? null, title: r.title, type: r.type || null, icon: r.icon || null })),
          employmentCategories: employment
            .filter((c) => c.name.trim())
            .map((c) => ({ name: c.name, shortName: c.shortName || null, count: Number(c.count) || 0 })),
        },
      });
      await refetch();
      setSavedNote(true);
      setTimeout(() => setSavedNote(false), 2500);
    } catch (e: any) {
      setSaveError(e?.message ?? "Failed to save");
    }
  };

  const handleSavePrograms = async () => {
    if (selectedId == null) return;
    setProgramError(null);
    try {
      await updatePrograms({
        variables: {
          supporterId: selectedId,
          programs: PROGRAM_CARDS.map((c) => ({
            card: c.card,
            description: programState[c.card]?.description?.trim() ? programState[c.card].description : null,
            // ProgramStatInput requires both fields; drop any incomplete rows.
            stats: (programState[c.card]?.stats ?? [])
              .filter((s) => s.label.trim() && s.value.trim())
              .map((s) => ({ label: s.label, value: s.value })),
          })),
        },
      });
      await refetch();
      setProgramSaved(true);
      setTimeout(() => setProgramSaved(false), 2500);
    } catch (e: any) {
      setProgramError(e?.message ?? "Failed to save");
    }
  };

  return (
    <Container style={{ padding: "2em 0em" }}>
      <Header as="h1">Partnerships</Header>

      {loading && <Segment basic loading style={{ minHeight: 80 }} />}

      {!loading && partnerships.length === 0 && (
        <Message>
          <Message.Header>No partnerships yet</Message.Header>
          <p>No organisations currently support your organisation.</p>
        </Message>
      )}

      {!loading && partnerships.length > 0 && (
        <>
          {partnerships.length > 1 && (
            <Form>
              <Form.Field>
                <label>Relationship</label>
                <Dropdown
                  placeholder="Select a relationship…"
                  fluid
                  selection
                  options={options}
                  value={selectedId ?? undefined}
                  onChange={(_, { value }) => setSelectedId(value as number)}
                />
              </Form.Field>
            </Form>
          )}

          {selected && (
            <div style={{ marginTop: "1.5em" }}>
              <Label color="grey" style={{ marginBottom: "1em" }}>
                <Icon name="handshake outline" />
                {selected.supporterName} · {scopeText(selected)}
              </Label>

              <Menu tabular>
                <Menu.Item
                  name="Community info"
                  active={activeTab === "info"}
                  onClick={() => setActiveTab("info")}
                />
                <Menu.Item
                  name="Service Arms"
                  active={activeTab === "programs"}
                  onClick={() => setActiveTab("programs")}
                />
              </Menu>

              <Message info icon style={{ marginTop: 0 }}>
                <Icon name={activeTab === "programs" ? "lock" : "users"} />
                <Message.Content>
                  <Message.Header>Who can see this</Message.Header>
                  {`${activeTab === "programs" ? "Visible to leadership only" : "Visible to anyone"} in${audienceSuffix} in the Joyya app.`}
                </Message.Content>
              </Message>

              {activeTab === "info" && (
                <>
                  {/* ── Description ── */}
                  <Segment>
                    <Header as="h3">Description</Header>
                    <Form>
                      <Form.TextArea
                        rows={4}
                        placeholder="e.g. The weaving cooperative in the northern neighbourhood, serving roughly 135 adults…"
                        value={desc}
                        onChange={(_, { value }) => setDesc(String(value ?? ""))}
                      />
                    </Form>
                  </Segment>

                  {/* ── Volunteering opportunities ── */}
                  <Segment>
                    <Header as="h3">Volunteering Opportunities</Header>
                    {volunteerRoles.length === 0 && (
                      <p style={{ color: "rgba(0,0,0,0.6)" }}>No opportunities yet.</p>
                    )}
                    {volunteerRoles.map((role) => (
                      <Form key={role.rowId} style={{ marginBottom: "0.75em" }}>
                        <Form.Group>
                          <Form.Input
                            width={7}
                            label="Title"
                            placeholder="e.g. Literacy Tutoring"
                            value={role.title}
                            onChange={(_, { value }) => updateVol(role.rowId, { title: value })}
                          />
                          <Form.Select
                            width={4}
                            label="Type"
                            options={VOLUNTEER_TYPES.map((t) => ({ key: t, value: t, text: t }))}
                            value={role.type}
                            onChange={(_, { value }) => updateVol(role.rowId, { type: String(value) })}
                          />
                          <Form.Select
                            width={4}
                            label="Icon"
                            placeholder="Select an icon"
                            options={iconOptions(role.icon)}
                            value={role.icon}
                            onChange={(_, { value }) => updateVol(role.rowId, { icon: String(value) })}
                          />
                          <Form.Field width={1}>
                            <label>&nbsp;</label>
                            <Button icon="trash" color="red" basic type="button" onClick={() => removeVol(role.rowId)} />
                          </Form.Field>
                        </Form.Group>
                        <InterestReadout count={role.interestedCount} interested={role.interested} />
                        <AdvertiseControl
                          saved={role.serverId != null}
                          lastAdvertisedAt={role.lastAdvertisedAt}
                          pendingAdvertisement={role.pendingAdvertisement}
                          onAdvertise={() => role.serverId != null && advertise(role.serverId)}
                        />
                      </Form>
                    ))}
                    <Button basic icon="plus" content="Add opportunity" type="button" onClick={addVol} />
                  </Segment>

                  {/* ── Employment ── */}
                  <Segment>
                    <Header as="h3">
                      Employment
                      <Label color="green" style={{ marginLeft: "0.75em" }} size="small">
                        {totalEmployed} of {workforce === "" ? "—" : workforce} employed
                      </Label>
                    </Header>
                    <Form>
                      <Form.Input
                        label="People in workforce (denominator)"
                        type="number"
                        width={5}
                        value={workforce}
                        onChange={(_, { value }) => setWorkforce(value === "" ? "" : Number(value))}
                      />
                    </Form>
                    <Divider />
                    {employment.length === 0 && (
                      <p style={{ color: "rgba(0,0,0,0.6)" }}>No categories yet.</p>
                    )}
                    {employment.map((cat) => (
                      <Form key={cat.rowId} style={{ marginBottom: "0.75em" }}>
                        <Form.Group>
                          <Form.Input
                            width={7}
                            label="Category"
                            placeholder="e.g. Textile & Weaving"
                            value={cat.name}
                            onChange={(_, { value }) => updateEmp(cat.rowId, { name: value })}
                          />
                          <Form.Input
                            width={4}
                            label="Short name"
                            placeholder="e.g. Textile"
                            value={cat.shortName}
                            onChange={(_, { value }) => updateEmp(cat.rowId, { shortName: value })}
                          />
                          <Form.Input
                            width={4}
                            label="Count"
                            type="number"
                            value={cat.count}
                            onChange={(_, { value }) => updateEmp(cat.rowId, { count: Number(value) || 0 })}
                          />
                          <Form.Field width={1}>
                            <label>&nbsp;</label>
                            <Button icon="trash" color="red" basic type="button" onClick={() => removeEmp(cat.rowId)} />
                          </Form.Field>
                        </Form.Group>
                      </Form>
                    ))}
                    <Button basic icon="plus" content="Add category" type="button" onClick={addEmp} />
                  </Segment>

                  <Button
                    primary
                    icon="save"
                    content="Save changes"
                    type="button"
                    loading={saving}
                    disabled={saving}
                    onClick={handleSave}
                  />
                  {savedNote && (
                    <Message positive size="tiny" style={{ marginTop: "1em" }}>
                      Saved.
                    </Message>
                  )}
                  {saveError && (
                    <Message negative size="tiny" style={{ marginTop: "1em" }}>
                      {saveError}
                    </Message>
                  )}
                </>
              )}

              {activeTab === "programs" && (
                <>
                  {PROGRAM_CARDS.map((c) => {
                    const ps = programState[c.card] ?? { description: "", stats: ensureSix([]) };
                    return (
                      <Segment key={c.card}>
                        <Header as="h3">{c.title}</Header>
                        <Form>
                          <Form.TextArea
                            label="Description"
                            rows={3}
                            value={ps.description}
                            onChange={(_, { value }) => setProgramDesc(c.card, String(value ?? ""))}
                          />
                          <Divider />
                          {ps.stats.map((stat, i) => (
                            <Form.Group key={i} widths="equal">
                              <Form.Input
                                label={i === 0 ? "Label" : undefined}
                                placeholder="Label"
                                value={stat.label}
                                onChange={(_, { value }) => setProgramStat(c.card, i, { label: value })}
                              />
                              <Form.Input
                                label={i === 0 ? "Value" : undefined}
                                placeholder="Value"
                                value={stat.value}
                                onChange={(_, { value }) => setProgramStat(c.card, i, { value })}
                              />
                            </Form.Group>
                          ))}
                        </Form>
                      </Segment>
                    );
                  })}

                  <Button
                    primary
                    icon="save"
                    content="Save programs"
                    type="button"
                    loading={savingPrograms}
                    disabled={savingPrograms}
                    onClick={handleSavePrograms}
                  />
                  {programSaved && (
                    <Message positive size="tiny" style={{ marginTop: "1em" }}>
                      Saved.
                    </Message>
                  )}
                  {programError && (
                    <Message negative size="tiny" style={{ marginTop: "1em" }}>
                      {programError}
                    </Message>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
}
